import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Accept, FileRejection, useDropzone } from 'react-dropzone';
import { ReactNode, useMemo } from 'react';
import { SxProps, styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getHighlightColorFromHex } from 'context/themeContext';
import { useTranslation } from 'react-i18next';
import { AUTURA_ASPHALT_DARK, AUTURA_ASPHALT_LIGHT } from 'utils';
import DeleteIcon from '@mui/icons-material/Delete';
import { CancelOutlined } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { keyframes } from '@emotion/react';

const rotatingBorderAnimation = keyframes`
  100% {
    background-position:
      12px 0,
      -12px 100%,
      0 -12px,
      100% 12px;
  }
`;

export const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export interface FileDropzoneProps {
  onDrop: (acceptedFiles: File[], rejectedFiles: FileRejection[]) => void;
  accept?: Accept | undefined;
  multiple?: boolean;
  maxSize?: number;
  state?: 'ready' | 'loading' | 'completed';
  onCancel?: () => void;
  readyLabel?: string;
  draggingLabel?: string;
  loadingLabel?: string;
  completedLabel?: string;
  caption?: string;
}

const FileDropzone = ({
  onDrop,
  accept = {},
  multiple = false,
  maxSize,
  state = 'ready',
  onCancel,
  readyLabel,
  draggingLabel,
  loadingLabel,
  completedLabel,
  caption,
}: FileDropzoneProps): ReactNode => {
  const { t } = useTranslation();
  const { palette, breakpoints } = useTheme();
  const mobile = useMediaQuery(breakpoints.down('lg'));

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    multiple,
    maxSize,
  });

  const dropzoneSx: SxProps = {
    borderWidth: '2px dashed',
    borderColor: isDragActive
      ? palette.info.main
      : getHighlightColorFromHex(palette.info.main),
    borderRadius: 1,
    p: 2,
    textAlign: 'center',
    background: `linear-gradient(90deg, ${palette.primary.main} 50%, transparent 0) repeat-x, linear-gradient(90deg, ${palette.primary.main} 50%, transparent 0) repeat-x, linear-gradient(0deg, ${palette.primary.main} 50%, transparent 0) repeat-y,linear-gradient(0deg, ${palette.primary.main} 50%, transparent 0) repeat-y`,
    backgroundSize: '12px 2px, 12px 2px, 2px 12px, 2px 12px',
    backgroundPosition: '0 0, 0 100%, 0 0, 100% 0',
    animation: isDragActive
      ? `${rotatingBorderAnimation} 0.6s linear infinite`
      : '',
  };

  const completedSx: SxProps = {
    border: `2px solid ${palette.success.main}`,
    borderRadius: 1,
    p: 2,
    textAlign: 'center',
  };

  const dropzoneLabel = useMemo(() => {
    if (isDragActive) {
      return draggingLabel || t('file_dropzone.dragging');
    }

    switch (state) {
      case 'ready':
        return mobile
          ? readyLabel || t('file_dropzone.title_mobile')
          : readyLabel || t('file_dropzone.title');
      case 'loading':
        return loadingLabel || t('file_dropzone.loading');
      case 'completed':
        return completedLabel || t('file_dropzone.completed');
      default:
        return readyLabel || t('file_dropzone.title');
    }
  }, [
    state,
    readyLabel,
    draggingLabel,
    loadingLabel,
    completedLabel,
    t,
    mobile,
  ]);

  const textColor = useMemo(() => {
    if (isDragActive) {
      return getHighlightColorFromHex(palette.primary.main);
    }
    if (state === 'completed') {
      return 'black';
    }
    if (state === 'loading') {
      return AUTURA_ASPHALT_LIGHT;
    }
    return palette.primary.main;
  }, [
    isDragActive,
    state,
    palette.info.main,
    palette.primary.main,
    palette.success.main,
  ]);

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <Box position={'relative'}>
      {state !== 'completed' && (
        <Box {...getRootProps()} sx={dropzoneSx}>
          <VisuallyHiddenInput
            {...getInputProps()}
            disabled={state !== 'ready'}
          />
          {state === 'loading' && <CircularProgress size={42} />}
          {state === 'ready' && (
            <CloudUploadIcon
              sx={{ fontSize: 42 }}
              htmlColor={
                isDragActive
                  ? getHighlightColorFromHex(palette.primary.main)
                  : palette.primary.main
              }
            />
          )}
          <Typography variant='h6' color={textColor}>
            {dropzoneLabel}
          </Typography>
        </Box>
      )}
      {state === 'completed' && (
        <Alert
          severity='success'
          variant='outlined'
          action={
            <IconButton
              aria-label='close'
              color='inherit'
              size='small'
              onClick={handleCancel}
            >
              <CloseIcon fontSize='inherit' />
            </IconButton>
          }
        >
          <Typography variant='body1' sx={{ px: 1 }}>
            {completedLabel}
          </Typography>
        </Alert>
      )}
      {caption && (
        <Typography variant='caption' color={'#000000CC'} pl={2}>
          {caption}
        </Typography>
      )}
    </Box>
  );
};

export default FileDropzone;
